import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaCheckCircle, FaUser } from "react-icons/fa";
import Layout from "../../components/Layout";
import Button from "../../components/ui/Button";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { PaymentMethod } from "../../interfaces/order.interface";
import { generateOrderId, saveOrder } from "../../utils/orderStorage";

const PAYMENT_OPTIONS: {
  value: PaymentMethod;
  labelKey: string;
  descKey: string;
}[] = [
  { value: "fps", labelKey: "payment_fps", descKey: "payment_fps_desc" },
  { value: "payme", labelKey: "payment_payme", descKey: "payment_payme_desc" },
  { value: "card", labelKey: "payment_card", descKey: "payment_card_desc" },
];

const Checkout = () => {
  const { t } = useTranslation(["checkout", "common", "products"]);
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { items, itemCount, subtotal, clearCart } = useCart();

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("fps");
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completedOrderId, setCompletedOrderId] = useState<string | null>(null);

  const displayName =
    user?.firstName || user?.lastName
      ? [user.firstName, user.lastName].filter(Boolean).join(" ")
      : user?.email ?? "";

  if (itemCount === 0 && !completedOrderId) {
    return (
      <Layout title={t("checkout:title")} showBackButton>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <h2 className="text-h2 mb-2 text-white">
            {t("checkout:empty_title")}
          </h2>
          <p className="text-body text-gray-400 mb-8 max-w-md">
            {t("checkout:empty_desc")}
          </p>
          <Link to="/products">
            <Button
              size="lg"
              className="bg-neon-green text-space hover:opacity-90"
            >
              {t("common:start_shopping")}
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  if (completedOrderId) {
    return (
      <Layout title={t("checkout:title")}>
        <div className="max-w-lg mx-auto text-center py-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-neon-green/10 border border-neon-green/40 mb-6">
            <FaCheckCircle className="w-10 h-10 text-neon-green" />
          </div>
          <h2 className="text-h2 text-white mb-3">
            {t("checkout:success_title")}
          </h2>
          <p className="text-body text-gray-400 mb-6">
            {t("checkout:success_desc")}
          </p>
          <div className="rounded-2xl bg-space-light/50 border border-white/10 p-4 mb-8">
            <p className="text-body-sm text-gray-400 mb-1">
              {t("checkout:order_number")}
            </p>
            <p className="text-h3 text-neon-green font-bold tabular-nums tracking-wide">
              {completedOrderId}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              className="bg-gradient-brand text-space hover:opacity-90"
              onClick={() => navigate("/orders")}
            >
              {t("checkout:view_orders")}
            </Button>
            <Link to="/products">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-white/20 text-gray-300"
              >
                {t("checkout:continue_shopping")}
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedTerms || !user) return;

    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 1200));

    const orderId = generateOrderId();
    saveOrder({
      id: orderId,
      createdAt: new Date().toISOString(),
      status: "processing",
      items: [...items],
      subtotal,
      customer: {
        userId: user.id,
        email: user.email,
        name: displayName,
      },
      paymentMethod,
    });

    clearCart();
    setCompletedOrderId(orderId);
    setIsSubmitting(false);
  };

  return (
    <Layout title={t("checkout:title")} showBackButton>
      <div className="max-w-6xl mx-auto">
        <Link
          to="/cart"
          className="inline-block text-body-sm text-gray-400 hover:text-neon-green transition-colors mb-6"
        >
          ← {t("checkout:back_to_cart")}
        </Link>

        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 lg:grid-cols-5 gap-8"
        >
          <div className="lg:col-span-3 space-y-6">
            <section className="rounded-2xl bg-space-light/50 border border-white/10 p-6">
              <h2 className="text-h4 text-white mb-4">
                {t("checkout:account_section")}
              </h2>

              {isAuthenticated && user ? (
                <>
                  <p className="text-body-sm text-gray-400 mb-5">
                    {t("checkout:account_hint")}
                  </p>
                  <div className="flex items-center gap-4 p-4 rounded-xl border border-neon-green/30 bg-neon-green/5">
                    <div className="shrink-0 w-10 h-10 rounded-full bg-neon-green/10 border border-neon-green/40 flex items-center justify-center">
                      <FaUser className="text-neon-green" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-body font-medium text-white truncate">
                        {displayName}
                      </p>
                      <p className="text-body-sm text-gray-400 truncate">
                        {user.email}
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-start gap-4 p-4 rounded-xl border border-neon-blue/30 bg-neon-blue/5">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-neon-blue/10 border border-neon-blue/40 flex items-center justify-center">
                    <FaUser className="text-neon-blue" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-body font-medium text-white mb-1">
                      {t("checkout:auth_prompt_title")}
                    </p>
                    <p className="text-body-sm text-gray-400 mb-4">
                      {t("checkout:login_required")}
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                      <Link
                        to="/login"
                        state={{ from: { pathname: "/checkout" } }}
                      >
                        <Button
                          size="sm"
                          className="bg-neon-green text-space hover:opacity-90"
                        >
                          {t("checkout:auth_login")}
                        </Button>
                      </Link>
                      <span className="text-body-sm text-gray-500">
                        {t("checkout:auth_or")}
                      </span>
                      <Link
                        to="/register"
                        state={{ from: { pathname: "/checkout" } }}
                      >
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-white/20 text-gray-300 hover:bg-white/5 hover:border-neon-green/40"
                        >
                          {t("checkout:auth_register")}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </section>

            <section className="rounded-2xl bg-space-light/50 border border-white/10 p-6">
              <h2 className="text-h4 text-white mb-5">
                {t("checkout:payment_section")}
              </h2>
              <div className="space-y-3">
                {PAYMENT_OPTIONS.map(({ value, labelKey, descKey }) => {
                  const selected = paymentMethod === value;
                  return (
                    <label
                      key={value}
                      className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-colors ${
                        selected
                          ? "border-neon-green bg-neon-green/5 glow-green"
                          : "border-white/10 bg-space/30 hover:border-white/20"
                      }`}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={value}
                        checked={selected}
                        onChange={() => setPaymentMethod(value)}
                        className="sr-only"
                      />
                      <span
                        className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selected ? "border-neon-green" : "border-white/30"
                        }`}
                      >
                        {selected && (
                          <span className="w-2.5 h-2.5 rounded-full bg-neon-green" />
                        )}
                      </span>
                      <div>
                        <p className="text-body font-medium text-white">
                          {t(`checkout:${labelKey}`)}
                        </p>
                        <p className="text-body-sm text-gray-400">
                          {t(`checkout:${descKey}`)}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </section>
          </div>

          <aside className="lg:col-span-2">
            <div className="lg:sticky lg:top-24 rounded-2xl bg-space-light/50 border border-white/10 p-6 space-y-5">
              <h2 className="text-h4 text-white">
                {t("checkout:order_summary")}
              </h2>

              <ul className="space-y-4 max-h-64 overflow-y-auto pr-1">
                {items.map((item) => (
                  <li key={item.productId} className="flex gap-3">
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="w-16 h-12 rounded-lg object-cover border border-white/10 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-body-sm text-white line-clamp-2">
                        {item.name}
                      </p>
                      <p className="text-caption text-gray-500 mt-0.5">
                        {t("checkout:qty")} × {item.quantity}
                      </p>
                    </div>
                    <p className="text-body-sm text-neon-green font-bold tabular-nums shrink-0">
                      HKD {(item.price * item.quantity).toLocaleString()}
                    </p>
                  </li>
                ))}
              </ul>

              <div className="border-t border-white/10 pt-4 space-y-2">
                <div className="flex justify-between text-body-sm text-gray-400">
                  <span>{t("checkout:subtotal")}</span>
                  <span className="tabular-nums">
                    HKD {subtotal.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-body font-medium text-white">
                    {t("checkout:total")}
                  </span>
                  <span className="text-h3 text-neon-green font-bold tabular-nums">
                    HKD {subtotal.toLocaleString()}
                  </span>
                </div>
              </div>

              <p className="text-caption text-neon-green/80 text-center">
                ⚡ {t("checkout:delivery_note")}
              </p>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedTerms}
                  onChange={(e) => setAgreedTerms(e.target.checked)}
                  className="mt-1 rounded border-white/30 bg-space text-neon-green focus:ring-neon-green"
                  required
                />
                <span className="text-body-sm text-gray-400">
                  {t("checkout:terms_prefix")}{" "}
                  <Link
                    to="/terms"
                    className="text-neon-green hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("checkout:terms_tnc_link")}
                  </Link>{" "}
                  {t("checkout:terms_joiner")}{" "}
                  <Link
                    to="/privacy"
                    className="text-neon-green hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("checkout:terms_privacy_link")}
                  </Link>
                  {t("checkout:terms_suffix")}
                </span>
              </label>

              <Button
                type="submit"
                size="lg"
                disabled={!agreedTerms || isSubmitting || !isAuthenticated}
                isLoading={isSubmitting}
                className="w-full bg-gradient-brand text-space hover:opacity-90 disabled:opacity-50"
              >
                {isSubmitting
                  ? t("checkout:processing")
                  : !isAuthenticated
                    ? t("checkout:login_required")
                    : t("checkout:place_order")}
              </Button>
            </div>
          </aside>
        </form>
      </div>
    </Layout>
  );
};

export default Checkout;
